import * as THREE from "three";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { InstanceBatch } from "../../../core/batches/instance_batch.js";
import { Vfx3DGlobalAtlas } from "../../../core/textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../../../core/types/core_types.js";

export const FireworkExplosionHeatLayout = {
  aPosRot:    AttributeSize.Vec4, // xyz: start_pos, w: rotation
  aVelDur:    AttributeSize.Vec4, // xyz: velocity, w: duration
  aScaleTime: AttributeSize.Vec4, // x: start_scale, y: end_scale, z: start_time, w: unused
} as const;

export class VfxFireworkExplosionHeatBatch extends InstanceBatch<typeof FireworkExplosionHeatLayout>
{
  private static readonly MAX_INSTANCES: number = 60;
  private static readonly RENDER_ORDER: number  = 20;

  // --- Visuals & Color Palette (такий самий перехід кольору, як у сфери) ---
  private static readonly COLOR_1: THREE.Color = new THREE.Color( 0xFFDC50 );  // Початковий колір (золотистий / жовтий)
  private static readonly COLOR_2: THREE.Color = new THREE.Color( 0xFF8C3C );  // Фінальний колір (помаранчевий)
  private static readonly FIRE_END_RATIO: number = 0.6;                        // Момент завершення колірного переходу (60% життя)
  private static readonly SMOG_INTENSITY: number = 1.0;                        // Множник яскравості світіння

  private static cached_geometry: THREE.BufferGeometry | null = null;

  public constructor()
  {
    const base_geometry = VfxFireworkExplosionHeatBatch.getSmogGeometry();

    let atlas_texture: THREE.Texture | null = null;
    const smog_uv = new THREE.Vector4( 0, 0, 1, 1 );
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.Smog );
      smog_uv.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
    }
    catch ( _e )
    {
      // Atlas will be populated lazily in spawnParticle if called before init
    }

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:          { value: 0.0 },
        uTexture:       { value: atlas_texture },
        uSmogUv:        { value: smog_uv },
        uColor1:        { value: VfxFireworkExplosionHeatBatch.COLOR_1 },
        uColor2:        { value: VfxFireworkExplosionHeatBatch.COLOR_2 },
        uFireEndRatio:  { value: VfxFireworkExplosionHeatBatch.FIRE_END_RATIO },
        uSmogIntensity: { value: VfxFireworkExplosionHeatBatch.SMOG_INTENSITY },
      },
      vertexShader: `
        uniform float uTime;
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform float uFireEndRatio;
        uniform float uSmogIntensity;

        attribute vec4 aPosRot;
        attribute vec4 aVelDur;
        attribute vec4 aScaleTime;

        varying vec2 vUv;
        varying vec3 vSmogColor;
        varying float vAlpha;

        void main() {
          float start_time = aScaleTime.z;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );

          vUv = uv;

          // 1. Колірний перехід як у сфери (COLOR_1 0xFFDC50 -> COLOR_2 0xFF8C3C)
          float color_t = clamp( progress / max( uFireEndRatio, 0.001 ), 0.0, 1.0 );
          vSmogColor = mix( uColor1, uColor2, smoothstep( 0.0, 1.0, color_t ) ) * uSmogIntensity;

          // 2. Крива прозорості як у Match3:
          float alpha_in = clamp( progress / 0.2, 0.0, 1.0 );
          float alpha_out = 1.0 - clamp( ( progress - 0.4 ) / 0.6, 0.0, 1.0 );
          vAlpha = alpha_in * alpha_out;

          // 3D-позиція частки у просторі зі сповільненням руху
          float ease_dist = 1.0 - pow( 1.0 - progress, 2.5 );
          vec3 computed_pos = aPosRot.xyz + aVelDur.xyz * duration * ease_dist;

          // Плавне зростання розміру клубка диму (Ease-out як у Match3)
          float scale_progress = 1.0 - pow( 1.0 - progress, 2.0 );
          float current_scale = mix( aScaleTime.x, aScaleTime.y, scale_progress );

          // 2D-кут нахилу площини білборда навколо власного центру
          float angle = aPosRot.w;
          float cos_a = cos( angle );
          float sin_a = sin( angle );

          vec2 rotated_quad = vec2(
            position.x * cos_a - position.y * sin_a,
            position.x * sin_a + position.y * cos_a
          );

          vec3 rotated_local = vec3( rotated_quad * current_scale, 0.0 );

          // Повноцінний 3D-білбординг у View Space (камера-фейсинг)
          vec4 mv_position = modelViewMatrix * vec4( computed_pos, 1.0 );
          mv_position.xyz += rotated_local;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform vec4 uSmogUv;

        varying vec2 vUv;
        varying vec3 vSmogColor;
        varying float vAlpha;

        void main() {
          vec2 uv_atlas = mix( uSmogUv.xy, uSmogUv.zw, vUv );
          vec4 tex = texture2D( uTexture, uv_atlas );

          gl_FragColor = vec4( vSmogColor * tex.rgb, tex.a * vAlpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxFireworkExplosionHeatBatch.MAX_INSTANCES, layout: FireworkExplosionHeatLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxFireworkExplosionHeatBatch.RENDER_ORDER;
  }

  private static getSmogGeometry(): THREE.BufferGeometry
  {
    if ( VfxFireworkExplosionHeatBatch.cached_geometry )
      return VfxFireworkExplosionHeatBatch.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxFireworkExplosionHeatBatch.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    start_time: number,
    duration: number,
    rotation?: number
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
    {
      try
      {
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
        const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.Smog );
        mat.uniforms.uSmogUv.value.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
      }
      catch ( _e )
      {
        // Atlas not yet ready
      }
    }

    mat.uniforms.uColor1.value        = VfxFireworkExplosionHeatBatch.COLOR_1;
    mat.uniforms.uColor2.value        = VfxFireworkExplosionHeatBatch.COLOR_2;
    mat.uniforms.uFireEndRatio.value  = VfxFireworkExplosionHeatBatch.FIRE_END_RATIO;
    mat.uniforms.uSmogIntensity.value = VfxFireworkExplosionHeatBatch.SMOG_INTENSITY;

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const angle = rotation ?? ( Math.random() * Math.PI * 2 );

    const pos_rot_attr    = this.geometry.getAttribute( "aPosRot" ).array as Float32Array;
    const vel_dur_attr    = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const scale_time_attr = this.geometry.getAttribute( "aScaleTime" ).array as Float32Array;

    const offset4 = idx * 4;

    pos_rot_attr[ offset4 ]     = start_pos.x;
    pos_rot_attr[ offset4 + 1 ] = start_pos.y;
    pos_rot_attr[ offset4 + 2 ] = start_pos.z;
    pos_rot_attr[ offset4 + 3 ] = angle;

    vel_dur_attr[ offset4 ]     = velocity.x;
    vel_dur_attr[ offset4 + 1 ] = velocity.y;
    vel_dur_attr[ offset4 + 2 ] = velocity.z;
    vel_dur_attr[ offset4 + 3 ] = duration;

    scale_time_attr[ offset4 ]     = start_scale;
    scale_time_attr[ offset4 + 1 ] = end_scale;
    scale_time_attr[ offset4 + 2 ] = start_time;
    scale_time_attr[ offset4 + 3 ] = 0.0;

    this.geometry.getAttribute( "aPosRot" ).needsUpdate    = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate    = true;
    this.geometry.getAttribute( "aScaleTime" ).needsUpdate = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
